"use client";

// UseCelo SDK - Unified Pricing and Feature Management for React Apps

// ===== PRIMARY PROVIDER SYSTEM =====
// Single, comprehensive provider for all UseCelo functionality
export {
  UseCeloProvider,
  useUseCelo,
  useFeature,
  Feature,
  useCheckout,
  useSubscriptionManagement
} from './providers/UseCeloProvider';

// ===== ENHANCED PRICING HOOKS =====
// Additional convenience hooks for pricing functionality
export {
  usePricing,
  useFeature as usePricingFeature,
  useSubscription as usePricingSubscription,
  useUsage
} from './hooks/usePricing';

// Pricing Table Configuration Hook
export { usePricingTableConfig } from './hooks/usePricingTableConfig';
export type { PricingTableConfig } from './hooks/usePricingTableConfig';

// Checkout Flow Hooks
export {
  useCheckoutFlow,
  useCheckoutSuccess,
  useCheckoutCancel
} from './hooks/useCheckoutFlow';
export type { CheckoutFlowOptions } from './hooks/useCheckoutFlow';

// ===== UI COMPONENTS =====
// Auth Integration
export { UserIdProvider } from './components/UserIdProvider';

export { PricingTable } from './components/PricingTable';
export type { PricingTableProps } from './components/PricingTable';

export { CheckoutButton, useCheckoutButton } from './components/CheckoutButton';

// Checkout Flow Components
export { CheckoutSuccess } from './components/CheckoutSuccess';
export type { CheckoutSuccessProps } from './components/CheckoutSuccess';
export { CheckoutCancel } from './components/CheckoutCancel';
export type { CheckoutCancelProps } from './components/CheckoutCancel';

// Feature Components
export {
  Feature as FeatureGate,
  MultiFeatures,
  withFeatureGate,
  TrialOnly,
  UpgradePrompt,
  PlanTier,
  SubscriptionGate
} from './components/Feature';
export type { FeatureProps } from './components/Feature';

// Usage Components
export {
  UsageDisplay,
  MultiUsageDisplay,
  UsageTracker,
  UsageControl,
  useUsageStats
} from './components/UsageComponents';

// Subscription Manager - Zero Configuration!
export { SubscriptionManager } from './components/subscription-manager';
export { SimplifiedSubscriptionManager } from './components/subscription-manager';
export type { SubscriptionManagerProps } from './components/subscription-manager';

// ===== PAYMENT SYSTEM =====
export { PaymentGatewayClient } from './api/payment-client';

// Auth is now handled via simple userId prop

// ===== API CLIENT =====
// For advanced use cases
export { UseCeloApiClient } from './api/client';

// ===== TYPE EXPORTS =====
export type {
  // Core configuration
  UseCeloConfig,
  UseCeloContextValue,

  // Feature types
  FeatureAccess,
  UserPlan,
  UserSubscription,
  FallbackBehavior,

  // Payment types
  PaymentGateway,
  IntegrationType,
  SubscriptionStatus,
  CheckoutOptions,
  CheckoutSession,
  GatewayInfo,
  GatewayCapabilities,
  SubscriptionDetails,
  PaymentMethod,
  Invoice,
  PortalSessionOptions,
  SubscriptionCancelOptions,
  SubscriptionPauseOptions,
  Currency,

  // Subscription Manager types
  SubscriptionDisplay,
  PlanDetails,
  PaymentMethodDisplay,
  InvoiceDisplay,
  CancelReason,
  PauseRequest,
  RetentionOffer,
  SubscriptionError,

  // API types
  User,
  Plan,
  Feature as FeatureType,
  Subscription,
  FeatureAccessResult,
  ApiResponse,

  // Aliases for backward compatibility
  UseCeloConfig as ApiConfig,
  UseCeloContextValue as ApiContextValue,
  UseCeloContextValue as UseCeloContextType
} from './types';

// ===== VERSION =====
export const VERSION = '0.6.0'; // Refactored SDK architecture